"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";

export const buttonVariants = cva(
  "relative inline-flex shrink-0 cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-lg font-bold text-base outline-none pointer-coarse:after:absolute pointer-coarse:after:size-full pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-55 data-loading:select-none data-loading:text-transparent sm:text-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        default: "h-10 px-4",
        icon: "size-10",
        "icon-lg": "size-11",
        "icon-sm": "size-9 rounded-md depth-3",
        "icon-xl":
          "size-12 [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-5",
        "icon-xs":
          "size-8 rounded-md depth-3 [&_svg:not([class*='size-'])]:size-4 sm:[&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-12 px-5 text-base sm:text-base",
        sm: "h-9 gap-1.5 rounded-md px-3 depth-3",
        xl: "h-14 rounded-xl px-6 text-lg depth-5 sm:text-lg [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-5",
        xs: "h-8 gap-1 rounded-md px-2.5 text-sm depth-3 sm:text-xs [&_svg:not([class*='size-'])]:size-4 sm:[&_svg:not([class*='size-'])]:size-3.5",
      },
      variant: {
        // Lilac face, darker lilac edge — the brand action.
        default:
          "pressable face-lilac-300 edge-lilac-600 text-ink *:data-[slot=button-loading-indicator]:text-ink",
        destructive:
          "pressable face-rose edge-rose-edge text-ink *:data-[slot=button-loading-indicator]:text-ink",
        dark: "pressable face-ink edge-black text-white *:data-[slot=button-loading-indicator]:text-white",
        "destructive-outline":
          "pressable face-surface edge-rose-edge text-destructive-foreground *:data-[slot=button-loading-indicator]:text-destructive-foreground",
        ghost:
          "border-2 border-transparent text-ink-soft hover:bg-paper-deep hover:text-ink data-pressed:bg-paper-deep *:data-[slot=button-loading-indicator]:text-foreground",
        link: "border-transparent text-primary underline-offset-4 hover:underline data-pressed:underline *:data-[slot=button-loading-indicator]:text-foreground h-auto px-0",
        outline:
          "pressable face-surface edge-line-strong text-ink *:data-[slot=button-loading-indicator]:text-ink",
        secondary:
          "pressable face-paper-deep edge-line-strong text-ink *:data-[slot=button-loading-indicator]:text-ink",
        sun: "pressable face-sun edge-sun-edge text-ink *:data-[slot=button-loading-indicator]:text-ink",
        mint: "pressable face-mint edge-mint-edge text-ink *:data-[slot=button-loading-indicator]:text-ink",
      },
    },
  }
);

export interface ButtonProps extends useRender.ComponentProps<"button"> {
  variant?: VariantProps<typeof buttonVariants>["variant"];
  size?: VariantProps<typeof buttonVariants>["size"];
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant,
      size,
      render,
      children,
      loading = false,
      disabled: disabledProp,
      ...props
    },
    forwardedRef
  ): React.ReactElement {
    const isDisabled: boolean = Boolean(loading || disabledProp);
    const typeValue: React.ButtonHTMLAttributes<HTMLButtonElement>["type"] =
      render ? undefined : "button";

    const defaultProps = {
      children: (
        <>
          {children}
          {loading && (
            <Spinner
              className="pointer-events-none absolute"
              data-slot="button-loading-indicator"
            />
          )}
        </>
      ),
      className: cn(buttonVariants({ className, size, variant })),
      "aria-disabled": loading || undefined,
      "data-loading": loading ? "" : undefined,
      "data-slot": "button",
      disabled: isDisabled,
      type: typeValue,
    };

    return useRender({
      defaultTagName: "button",
      props: mergeProps<"button">(defaultProps, props),
      ref: forwardedRef,
      render,
    });
  }
);
